import * as echarts from 'echarts';
import Papa from 'papaparse';
import Uppy from '@uppy/core';
import DragDrop from '@uppy/drag-drop';
import DropTarget from '@uppy/drop-target';
import { encode, decode } from "@msgpack/msgpack";

import '@uppy/core/dist/style.min.css';
import '@uppy/drag-drop/dist/style.min.css';
import '../css/styles.css';
import { processFile } from './messagePack';
import { is24T232File, process24T232File } from './parser24T232';
import '@andypf/json-viewer';


// convierte un uint8array a un uint16array
function uint8ToUint16(uint8) {
  return new Int16Array((new Uint8Array(uint8).buffer))
}

let dataset = [];
let currentData = null;
let currentConfig = null;

// Configuración de escala por defecto (valores genéricos)
const DEFAULT_SCALE_CONFIG = {
  voltage: { fields: [], factor: 1, removeOffset: false },
  current: { fields: [], factor: 1, removeOffset: false }
};

let currentScaleConfig = JSON.parse(JSON.stringify(DEFAULT_SCALE_CONFIG));

// Obtiene la configuración de escala desde el config actual o devuelve valores por defecto
function getScaleConfigFromCurrentConfig() {
  if (!currentConfig || !currentConfig.scaleConfig) {
    return JSON.parse(JSON.stringify(DEFAULT_SCALE_CONFIG));
  }
  const sc = currentConfig.scaleConfig;
  return {
    voltage: {
      fields: sc.voltage?.fields || [],
      factor: sc.voltage?.factor ?? 1,
      removeOffset: sc.voltage?.removeOffset ?? false
    },
    current: {
      fields: sc.current?.fields || [],
      factor: sc.current?.factor ?? 1,
      removeOffset: sc.current?.removeOffset ?? false
    }
  };
}

var chartDom = document.getElementById('main');
var myChart = echarts.init(chartDom, 'dark');
var option;

// Registrar transform personalizado para escalar datos
echarts.registerTransform({
  type: 'scale:apply',
  transform: function (params) {
    const upstream = params.upstream;
    const config = params.config || {};
    const voltageFields = config.voltageFields || [];
    const currentFields = config.currentFields || [];
    const voltageFactor = config.voltageFactor ?? 1;
    const currentFactor = config.currentFactor ?? 1;
    const voltageRemoveOffset = config.voltageRemoveOffset ?? false;
    const currentRemoveOffset = config.currentRemoveOffset ?? false;
    
    const rawData = upstream.cloneRawData();
    const dimensions = upstream.cloneAllDimensionInfo().map(d => d.name);
    
    // Calcular offsets si es necesario
    const offsets = {};
    const fieldsToOffset = [
      ...(voltageRemoveOffset ? voltageFields : []),
      ...(currentRemoveOffset ? currentFields : [])
    ];
    
    fieldsToOffset.forEach(field => {
      const idx = dimensions.indexOf(field);
      if (idx >= 0 && rawData.length > 0) {
        const sum = rawData.reduce((acc, row) => acc + (Number(row[idx]) || 0), 0);
        offsets[field] = sum / rawData.length;
      }
    });
    
    // Aplicar transformación
    const result = rawData.map(row => {
      const newRow = [...row];
      dimensions.forEach((dim, idx) => {
        if (voltageFields.includes(dim)) {
          const offset = offsets[dim] || 0;
          newRow[idx] = (Number(row[idx]) - offset) * voltageFactor;
        } else if (currentFields.includes(dim)) {
          const offset = offsets[dim] || 0;
          newRow[idx] = (Number(row[idx]) - offset) * currentFactor;
        }
      });
      return newRow;
    });
    
    return { data: result };
  }
});

let resizeRafId = null;
function scheduleChartResize() {
  if (!myChart) return;
  if (resizeRafId !== null) {
    cancelAnimationFrame(resizeRafId);
  }
  resizeRafId = requestAnimationFrame(() => {
    myChart.resize();
    resizeRafId = null;
  });
}

window.addEventListener('resize', scheduleChartResize);

const ADC_REF_V = 3.3;
const ADC_FULL_SCALE = 4096;

function isWaveExportEnabled() {
  return Boolean(currentData)
    && currentData.productID === '24P002'
    && Number(currentData.type) === 5;
}

function isScaleConfigEnabled() {
  return Boolean(currentConfig) && Boolean(currentConfig.scaleConfig);
}

function updateToolboxVisibility() {
  // Muestra/oculta los botones del toolbox según el tipo de archivo
  myChart.setOption({
    toolbox: {
      feature: {
        dataView: {
          show: isWaveExportEnabled()
        },
        myScale: {
          show: isScaleConfigEnabled()
        }
      }
    }
  });
}

function updateWaveExportToolboxVisibility() {
  updateToolboxVisibility();
}

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

option = {
  grid: {
    bottom: 80
  },
  toolbox: {
    show: true,
    feature: {
      dataZoom: { show: true },
      dataView: {
        show: false,
        title: 'Exportar onda',
        readOnly: true,
        lang: ['Exportar onda', 'Cerrar', 'Actualizar'],
        optionToContent: function () {
          const wrap = document.createElement('div');
          wrap.style.padding = '12px';
          wrap.style.fontFamily = 'Consolas, Menlo, Monaco, "Courier New", monospace';

          if (!currentData || !currentConfig) {
            const msg = document.createElement('div');
            msg.textContent = 'No hay datos cargados. Carga un .mpk primero.';
            wrap.appendChild(msg);
            return wrap;
          }

          if (currentData.productID !== '24P002' || Number(currentData.type) !== 5) {
            const msg = document.createElement('div');
            msg.textContent = `Documento actual: ${currentData.productID} tipo ${currentData.type}. Esta vista aplica sólo a 24P002 tipo 5.`;
            wrap.appendChild(msg);
            return wrap;
          }

          const fields = Array.isArray(currentConfig?.fields) ? currentConfig.fields : [];
          const row = document.createElement('div');
          row.style.display = 'flex';
          row.style.gap = '8px';
          row.style.alignItems = 'center';
          row.style.marginBottom = '10px';

          const label = document.createElement('span');
          label.textContent = 'Serie:';
          label.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, Arial';
          row.appendChild(label);

          const select = document.createElement('select');
          select.style.minWidth = '160px';
          fields.forEach((f) => {
            const opt = document.createElement('option');
            opt.value = f;
            opt.textContent = f;
            select.appendChild(opt);
          });
          row.appendChild(select);

          const btnDownload = document.createElement('button');
          btnDownload.textContent = 'Descargar';
          row.appendChild(btnDownload);

          const btnCopy = document.createElement('button');
          btnCopy.textContent = 'Copiar';
          row.appendChild(btnCopy);

          wrap.appendChild(row);

          const hint = document.createElement('div');
          hint.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, Arial';
          hint.style.fontSize = '12px';
          hint.style.opacity = '0.85';
          hint.style.marginBottom = '8px';
          hint.textContent = 'Formato: [tiempo_segundos] [valor_escalado]';
          wrap.appendChild(hint);

          const textarea = document.createElement('textarea');
          textarea.style.width = '100%';
          textarea.style.height = '320px';
          textarea.style.boxSizing = 'border-box';
          textarea.readOnly = true;
          wrap.appendChild(textarea);

          const refresh = () => {
            const built = buildWaveTextFor24P002Type5(select.value);
            textarea.value = built?.error ? built.error : built.text;
          };

          btnDownload.onclick = () => {
            const built = buildWaveTextFor24P002Type5(select.value);
            if (built?.error) {
              alert(built.error);
              return;
            }
            downloadTextFile(built.filename, built.text);
          };

          btnCopy.onclick = async () => {
            const built = buildWaveTextFor24P002Type5(select.value);
            if (built?.error) {
              alert(built.error);
              return;
            }
            try {
              await navigator.clipboard.writeText(built.text);
            } catch (_) {
              textarea.focus();
              textarea.select();
              document.execCommand('copy');
            }
          };

          select.onchange = refresh;
          refresh();

          return wrap;
        }
      },
      restore: {show: true},
      saveAsImage: { show: true },
      myScale: {
        show: false,
        title: 'Escala',
        icon: 'path://M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z',
        onclick: function () {
          showScalePopup();
        }
      },
      myInfo: {
        show: true,
        title: 'Info',
        icon: 'path://M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z',
        onclick: function () {
          showInfoPopup();
        }
      }
    }
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      animation: false,
      label: {
        backgroundColor: '#505765'
      }
    }
  },
  dataZoom: [
    {
      show: true,
      realtime: true,
      type: 'inside',
    }
  ],
  xAxis: [
    {
      type: 'category',
      boundaryGap: false,
      axisLine: { onZero: false },
    }
  ],
  yAxis: [
    {
      name: '',
      type: 'value'
    },
  ],
  dataset: {
    // Provide a set of data.
    source: dataset
  },
  series: [],
  animationEasing: 'elasticOut',
};

option && myChart.setOption(option);
updateWaveExportToolboxVisibility();

const uppy = new Uppy({ debug: true, autoProceed: false })
  //.use(DragDrop, { target: '#drag-drop' });

uppy.use(DropTarget, {
	target: document.body,
});

uppy.on('file-added', async (file) => {
	console.log(`File added: ${file.name}`,file);
  document.getElementById('popupOverlay').style.display = 'none';
  document.getElementById('scalePopupOverlay').style.display = 'none';

  if (file) {
    
    if (file.name.includes('.mpk')) {
      let binFile = await file.data.arrayBuffer();
      let result = processFile(binFile, file.name);

      if (result && result.option) {
        currentData = result.deserializedObj;
        currentConfig = result.config;
        
        // Inicializar configuración de escala desde el config del archivo
        currentScaleConfig = getScaleConfigFromCurrentConfig();
        
        // Si hay scaleConfig definido, configurar datasets con transform para escala
        if (currentConfig.scaleConfig) {
          const originalDataset = result.option.dataset;
          
          result.option.dataset = [
            {
              id: 'raw',
              source: originalDataset.source,
              dimensions: originalDataset.dimensions
            },
            {
              id: 'scaled',
              fromDatasetId: 'raw',
              transform: {
                type: 'scale:apply',
                config: {
                  voltageFields: currentScaleConfig.voltage.fields,
                  currentFields: currentScaleConfig.current.fields,
                  voltageFactor: currentScaleConfig.voltage.factor,
                  currentFactor: currentScaleConfig.current.factor,
                  voltageRemoveOffset: currentScaleConfig.voltage.removeOffset,
                  currentRemoveOffset: currentScaleConfig.current.removeOffset
                }
              }
            }
          ];
          
          // Actualizar series para usar el dataset escalado
          if (result.option.series) {
            result.option.series = result.option.series.map(s => ({
              ...s,
              datasetIndex: 1 // Usar el dataset 'scaled'
            }));
          }
        }
        
        myChart.setOption(result.option, { notMerge: false, replaceMerge: ['series', 'xAxis', 'yAxis', 'grid', 'dataZoom', 'dataset'] });
        updateWaveExportToolboxVisibility();
      }

    } else if (is24T232File(file.name)) {
      // Archivo del producto 24T232 (formato Slv-{n}_{timestamp}.txt)
      const arrayBuffer = await file.data.arrayBuffer();
      const result = process24T232File(arrayBuffer, file.name);

      if (result && result.option) {
        myChart.setOption(result.option, { notMerge: false, replaceMerge: ['series', 'xAxis', 'yAxis', 'grid', 'dataZoom', 'dataset'] });
        currentData = result.data;
        currentConfig = result.config;
      } else {
        console.warn('No se pudo procesar el archivo 24T232:', file.name);
        currentData = null;
        currentConfig = null;
      }
      updateWaveExportToolboxVisibility();

    } else if (file.name.includes('.txt') || file.name.includes('.txs')) {
      currentData = null;
      currentConfig = null;
      updateWaveExportToolboxVisibility();
      const separador = '\r\n*****---*****$\r\n';

      let text = await file.data.text();
      let post = text.indexOf(separador);

      console.log("fault");
      

      const arrayBuf = await file.data.slice(post + separador.length).arrayBuffer();
      var int16Array = new Int16Array(arrayBuf, 0, Math.floor(arrayBuf.byteLength / 2));

      const numCols = 1;
      dataset = [...int16Array].map((row, index) => {
        return [index, row]
      });

      const series = [];
      const legend = [];

      for (let i = 0; i < numCols; i++) {
        legend.push(`serie ${i + 1}`);
        series.push({
          name: `serie ${i + 1}`,
          type: 'line',
          large: true,
          sampling: 'lttb',
          smooth: true,
          lineStyle: {
            width: 1
          },
          areaStyle: null
        });
      }

      myChart.setOption({
        title: { text: '' },
        dataset: {
          // Provide a set of data.
          source: dataset
        },
        legend: {
          data: legend,
          itemGap: 5
        },
        dataZoom: [{ type: 'inside', realtime: true }],
        series: series,
      }, { replaceMerge: ['series', 'dataZoom', 'dataset'] });
    } else {
      currentData = null;
      currentConfig = null;
      updateWaveExportToolboxVisibility();
      Papa.parse(file.data, {
        dynamicTyping: true,
        complete: function (results) {
          dataset = results.data;
          if (file.name.includes('fft_')) {
          
            myChart.setOption({
              title: { text: '' },
              dataset: {
                // Provide a set of data.
                source: dataset
              },
              dataZoom: [{ type: 'inside', realtime: true }],
              series: [
                {
                  name: '',
                  type: 'line',
                  large: true,
                  //step: 'middle',
                  smooth: true,
                  sampling: 'lttb',
                  areaStyle: {},
                  lineStyle: {
                    width: 1
                  }
                }
              ],
            }, { replaceMerge: ['series', 'dataZoom', 'dataset'] });

          } else if (file.name.includes('injection_')) {
            // agrega el index en la primera columna del dataset
            const numCols = dataset[0].length;
            dataset = dataset.map((row, index) => [index, ...row]);

            console.log(dataset);

            const series = [];
            const legend = []
          
            for (let i = 0; i < numCols; i++) {
              legend.push(`serie ${i + 1}`);
              series.push({
                name: `serie ${i + 1}`,
                type: 'line',
                large: true,
                sampling: 'lttb',
                smooth: true,
                lineStyle: {
                  width: 1
                },
                areaStyle: null
              });
            }

            myChart.setOption({
              title: { text: '' },
              dataset: {
                // Provide a set of data.
                source: dataset
              },
              legend: {
                data: legend,
                itemGap: 5
              },
              dataZoom: [{ type: 'inside', realtime: true }],
              series: series,
            }, { replaceMerge: ['series', 'dataZoom', 'dataset'] });
          }
        }
      });
    }
    
  }

  uppy.cancelAll({ reason: 'user' });
  
});

function deletePropertyPath(obj, path) {
  const parts = path.split('.');
  const last = parts.pop();
  let current = obj;
  for (const part of parts) {
      if (current[part] === undefined) return;
      current = current[part];
  }
  if (current) delete current[last];
}

function showInfoPopup() {
  if (!currentData) {
      alert('No data loaded');
      return;
  }

  const excludedKeys = new Set(currentConfig?.fields || []);
  const excludedPaths = currentConfig?.excludedFields || [];

  function safeClone(obj) {
      if (obj === null || typeof obj !== 'object') return obj;
      
      if (ArrayBuffer.isView(obj)) {
          return `[TypedArray(${obj.byteLength} bytes)]`;
      }
      
      if (Array.isArray(obj)) {
          return obj.map(safeClone);
      }
      
      const clone = {};
      for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
              clone[key] = safeClone(obj[key]);
          }
      }
      return clone;
  }
  
  const dataToShow = safeClone(currentData);
  
  // Remove graphed fields (top level)
  excludedKeys.forEach(key => {
      delete dataToShow[key];
  });
  
  // Remove excluded paths
  excludedPaths.forEach(path => {
      deletePropertyPath(dataToShow, path);
  });
  
  const container = document.getElementById('jsonContainer');
  container.innerHTML = ''; // Clear previous content

  const jsonViewer = document.createElement("andypf-json-viewer");
  jsonViewer.id = "json-viewer";
  jsonViewer.expanded = 1;
  jsonViewer.indent = 2;
  jsonViewer.showDataTypes = true;
  jsonViewer.theme = "monokai";
  jsonViewer.showToolbar = true;
  jsonViewer.showSize = true;
  jsonViewer.showCopy = true;
  jsonViewer.expandIconType = "square";
  
  // We need to set data after element creation
  jsonViewer.data = dataToShow;
  
  container.appendChild(jsonViewer);
  
  document.getElementById('popupOverlay').style.display = 'block';
}

function closeInfoPopup() {
  document.getElementById('popupOverlay').style.display = 'none';
}

document.getElementById('popupOverlay').addEventListener('click', (e) => {
  if (e.target.id === 'popupOverlay') {
    closeInfoPopup();
  }
});

document.getElementById('closePopup').addEventListener('click', closeInfoPopup);

// ===================== SCALE CONFIGURATION =====================

function showScalePopup() {
  if (!isScaleConfigEnabled()) {
    alert('La configuración de escala no está disponible para este tipo de archivo');
    return;
  }

  const container = document.getElementById('scaleConfigContainer');
  container.innerHTML = '';

  const createFieldGroup = (title, prefix, config) => {
    const group = document.createElement('div');
    group.className = 'field-group';

    const titleEl = document.createElement('h4');
    titleEl.textContent = title;
    titleEl.className = 'field-group__title';
    group.appendChild(titleEl);

    // Factor de conversión
    const factorRow = document.createElement('div');
    factorRow.className = 'form-row';

    const factorLabel = document.createElement('label');
    factorLabel.textContent = 'Factor:';
    factorLabel.className = 'form-label';
    factorRow.appendChild(factorLabel);

    const factorInput = document.createElement('input');
    factorInput.type = 'number';
    factorInput.step = 'any';
    factorInput.value = config.factor;
    factorInput.id = `${prefix}Factor`;
    factorInput.className = 'input-text';
    factorRow.appendChild(factorInput);

    group.appendChild(factorRow);

    // Remove offset - Switch toggle
    const offsetRow = document.createElement('div');
    offsetRow.className = 'form-row';

    const offsetLabel = document.createElement('label');
    offsetLabel.textContent = 'Eliminar offset:';
    offsetLabel.className = 'form-label';
    offsetRow.appendChild(offsetLabel);

    // Switch container
    const switchLabel = document.createElement('label');
    switchLabel.className = 'switch';

    const offsetCheck = document.createElement('input');
    offsetCheck.type = 'checkbox';
    offsetCheck.checked = config.removeOffset;
    offsetCheck.id = `${prefix}Offset`;
    offsetCheck.className = 'switch__input';

    const slider = document.createElement('span');
    slider.className = 'switch__slider';

    switchLabel.appendChild(offsetCheck);
    switchLabel.appendChild(slider);
    offsetRow.appendChild(switchLabel);

    group.appendChild(offsetRow);

    return group;
  };

  const voltageFieldsLabel = currentScaleConfig.voltage.fields.join(', ') || 'Voltaje';
  const currentFieldsLabel = currentScaleConfig.current.fields.join(', ') || 'Corriente';

  container.appendChild(createFieldGroup(
    `Voltaje (${voltageFieldsLabel})`,
    'voltage',
    currentScaleConfig.voltage
  ));

  container.appendChild(createFieldGroup(
    `Corriente (${currentFieldsLabel})`,
    'current',
    currentScaleConfig.current
  ));

  document.getElementById('scalePopupOverlay').style.display = 'block';
}

function applyScaleAndRedraw() {
  // Leer valores del formulario
  const voltageFactor = parseFloat(document.getElementById('voltageFactor').value) || 1;
  const voltageOffset = document.getElementById('voltageOffset').checked;
  const currentFactor = parseFloat(document.getElementById('currentFactor').value) || 1;
  const currentOffset = document.getElementById('currentOffset').checked;

  // Preservar los fields y actualizar factor/offset
  currentScaleConfig.voltage = { 
    fields: currentScaleConfig.voltage.fields,
    factor: voltageFactor, 
    removeOffset: voltageOffset 
  };
  currentScaleConfig.current = { 
    fields: currentScaleConfig.current.fields,
    factor: currentFactor, 
    removeOffset: currentOffset 
  };

  // Actualizar el transform en el dataset sin modificar los datos originales
  if (currentConfig) {
    myChart.setOption({
      dataset: [
        {
          id: 'raw',
          // Los datos originales ya están cargados, no los tocamos
        },
        {
          id: 'scaled',
          fromDatasetId: 'raw',
          transform: {
            type: 'scale:apply',
            config: {
              voltageFields: currentScaleConfig.voltage.fields,
              currentFields: currentScaleConfig.current.fields,
              voltageFactor: currentScaleConfig.voltage.factor,
              currentFactor: currentScaleConfig.current.factor,
              voltageRemoveOffset: currentScaleConfig.voltage.removeOffset,
              currentRemoveOffset: currentScaleConfig.current.removeOffset
            }
          }
        }
      ]
    });
  }

  document.getElementById('scalePopupOverlay').style.display = 'none';
}

document.getElementById('scalePopupOverlay').addEventListener('click', (e) => {
  if (e.target.id === 'scalePopupOverlay') {
    document.getElementById('scalePopupOverlay').style.display = 'none';
  }
});

document.getElementById('scaleApplyBtn').addEventListener('click', applyScaleAndRedraw);
document.getElementById('scaleCancelBtn').addEventListener('click', () => {
  document.getElementById('scalePopupOverlay').style.display = 'none';
});
